import React from "react";
import { create } from "zustand";
import Text from "@src/lib/ui/Text";
import { Switch } from "@src/lib/ui/Switch";
import { useIsStaff } from "@src/features/app/hooks/useIsStaff";

const RECOMMENDATIONS_MOCK_DATA_STORAGE_KEY = "recommendations-use-mock-data";

type RecommendationsMockDataState = {
  useMockData: boolean;
  setUseMockData: (value: boolean) => void;
};

// Staff-only toggle that swaps the Recommendations feature over to rich, fully
// self-contained mock data (no backend calls) so the flow can be demoed and
// designed without a connected Guest Feedback source. Mirrors the reporting
// mock-data store: a single persisted boolean the feature's query-backed
// components read to branch between live and mock data.
export const useRecommendationsMockData = create<RecommendationsMockDataState>(
  (set) => ({
    useMockData:
      typeof window !== "undefined" &&
      localStorage.getItem(RECOMMENDATIONS_MOCK_DATA_STORAGE_KEY) === "true",
    setUseMockData: (value: boolean) => {
      localStorage.setItem(
        RECOMMENDATIONS_MOCK_DATA_STORAGE_KEY,
        String(value),
      );
      set({ useMockData: value });
    },
  }),
);

// The staff-only switch that flips the store, shared by the Recommendations
// and Guest Feedback pages (one persisted flag drives both). Renders nothing
// for non-staff users.
export const RecommendationsMockDataToggle: React.FC = () => {
  const { isStaff } = useIsStaff();
  const { useMockData, setUseMockData } = useRecommendationsMockData();
  if (!isStaff) return null;
  return (
    <div className="flex items-center justify-end gap-2">
      <Text variant="fine-print-description" className="text-text-tertiary">
        Use mock data
      </Text>
      <Switch
        aria-label="Use mock data"
        checked={useMockData}
        onCheckedChange={setUseMockData}
        size="sm"
      />
    </div>
  );
};
